import { useState, useEffect } from "react";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const emptyAlbumForm = {
  title: "",
  description: "",
  event_date: "",
  display_order: 0,
};

function GalleryPage() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Album add/edit modal
  const [showAlbumModal, setShowAlbumModal] = useState(false);
  const [editingAlbumId, setEditingAlbumId] = useState(null);
  const [albumForm, setAlbumForm] = useState(emptyAlbumForm);
  const [coverFile, setCoverFile] = useState(null);

  // Photos-within-album modal
  const [showPhotosModal, setShowPhotosModal] = useState(false);
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  useEffect(() => {
    fetchAlbums();
  }, []);

  const fetchAlbums = async () => {
    try {
      const res = await axiosInstance.get("/api/gallery/albums");
      setAlbums(res.data);
    } catch (err) {
      toast.error("Failed to load albums");
    } finally {
      setLoading(false);
    }
  };

  // ---------- ALBUM CRUD ----------

  const openAddAlbum = () => {
    setEditingAlbumId(null);
    setAlbumForm(emptyAlbumForm);
    setCoverFile(null);
    setShowAlbumModal(true);
  };

  const handleAlbumFormChange = (e) => {
    const { name, value } = e.target;
    setAlbumForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAlbumSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const formData = new FormData();
    formData.append("title", albumForm.title);
    formData.append("description", albumForm.description);
    formData.append("display_order", albumForm.display_order);
    if (albumForm.event_date)
      formData.append("event_date", albumForm.event_date);
    if (coverFile) formData.append("cover_image", coverFile);

    try {
      await axiosInstance.post("/api/gallery/albums", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Album created");
      setShowAlbumModal(false);
      fetchAlbums();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create album");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlbum = async (id, title) => {
    if (
      !window.confirm(
        `Delete album "${title}" and ALL its photos? This cannot be undone.`,
      )
    )
      return;
    try {
      await axiosInstance.delete(`/api/gallery/albums/${id}`);
      toast.success("Album deleted");
      fetchAlbums();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete album");
    }
  };

  // ---------- PHOTOS WITHIN AN ALBUM ----------

  const openPhotosModal = async (album) => {
    try {
      const res = await axiosInstance.get(`/api/gallery/albums/${album.id}`);
      setActiveAlbum(res.data);
      setShowPhotosModal(true);
    } catch (err) {
      toast.error("Failed to load album photos");
    }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    setUploadingPhoto(true);
    try {
      await axiosInstance.post(
        `/api/gallery/albums/${activeAlbum.id}/photos`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );
      toast.success("Photo added");
      // Refresh the active album's photo list
      const res = await axiosInstance.get(
        `/api/gallery/albums/${activeAlbum.id}`,
      );
      setActiveAlbum(res.data);
      fetchAlbums(); // updates cover/count in the background grid too, if needed
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to upload photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axiosInstance.delete(`/api/gallery/photos/${photoId}`);
      toast.success("Photo deleted");
      const res = await axiosInstance.get(
        `/api/gallery/albums/${activeAlbum.id}`,
      );
      setActiveAlbum(res.data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete photo");
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Photo Gallery</h4>
        <button className="btn btn-primary" onClick={openAddAlbum}>
          <i className="bi bi-plus-lg me-1"></i> New Album
        </button>
      </div>

      <div className="row g-3">
        {albums.length === 0 ? (
          <p className="text-muted">No albums created yet.</p>
        ) : (
          albums.map((album) => (
            <div className="col-12 col-sm-6 col-lg-4" key={album.id}>
              <div className="card border-0 shadow-sm h-100">
                <div
                  role="button"
                  onClick={() => openPhotosModal(album)}
                  style={{
                    height: "160px",
                    overflow: "hidden",
                    background: "#f0f0f0",
                  }}
                >
                  {album.cover_image_path ? (
                    <img
                      src={`${process.env.REACT_APP_API_URL}/${album.cover_image_path}`}
                      alt={album.title}
                      className="w-100 h-100"
                      style={{ objectFit: "cover" }}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <i
                        className="bi bi-images text-muted"
                        style={{ fontSize: "2rem" }}
                      ></i>
                    </div>
                  )}
                </div>
                <div className="card-body">
                  <h6 className="mb-1">{album.title}</h6>
                  <p className="text-muted small mb-2">
                    {album.description || "—"}
                  </p>
                  <div className="d-flex justify-content-between align-items-center">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => openPhotosModal(album)}
                    >
                      <i className="bi bi-images me-1"></i> Manage Photos
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDeleteAlbum(album.id, album.title)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Album Modal */}
      {showAlbumModal && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <form onSubmit={handleAlbumSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">New Album</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowAlbumModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={albumForm.title}
                        onChange={handleAlbumFormChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        name="description"
                        rows="2"
                        value={albumForm.description}
                        onChange={handleAlbumFormChange}
                      ></textarea>
                    </div>
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Event Date</label>
                        <input
                          type="date"
                          className="form-control"
                          name="event_date"
                          value={albumForm.event_date}
                          onChange={handleAlbumFormChange}
                        />
                      </div>
                      <div className="col-6 mb-3">
                        <label className="form-label">Display Order</label>
                        <input
                          type="number"
                          className="form-control"
                          name="display_order"
                          value={albumForm.display_order}
                          onChange={handleAlbumFormChange}
                        />
                      </div>
                    </div>
                    <div className="mb-1">
                      <label className="form-label">Cover Image</label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={(e) => setCoverFile(e.target.files[0])}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowAlbumModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? "Creating..." : "Create Album"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}

      {/* Manage Photos Modal */}
      {showPhotosModal && activeAlbum && (
        <>
          <div className="modal show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{activeAlbum.title} — Photos</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPhotosModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <label className="btn btn-primary btn-sm mb-3">
                    {uploadingPhoto ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>{" "}
                        Uploading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-upload me-1"></i> Add Photo
                      </>
                    )}
                    <input
                      type="file"
                      accept=".png,.jpg,.jpeg,.webp"
                      hidden
                      onChange={handleAddPhoto}
                      disabled={uploadingPhoto}
                    />
                  </label>

                  <div className="row g-2">
                    {activeAlbum.photos?.length === 0 ? (
                      <p className="text-muted">No photos in this album yet.</p>
                    ) : (
                      activeAlbum.photos?.map((photo) => (
                        <div className="col-4 col-md-3" key={photo.id}>
                          <div className="position-relative">
                            <img
                              src={`${process.env.REACT_APP_API_URL}/${photo.image_path}`}
                              alt={photo.caption || ""}
                              className="w-100 rounded"
                              style={{ height: "90px", objectFit: "cover" }}
                            />
                            <button
                              className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 py-0 px-1"
                              onClick={() => handleDeletePhoto(photo.id)}
                              style={{ lineHeight: 1 }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowPhotosModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop show"></div>
        </>
      )}
    </div>
  );
}

export default GalleryPage;
